import React, {useCallback, useEffect, useMemo, useState} from "react";
import {ArrowDownOutlined, ArrowUpOutlined} from "@ant-design/icons";

import ProfileHeader from "../../components/profile/ProfileHeader";
import ProfileCard from "../../components/profile/ProfileCard";
import TransactionsCard from "../../components/profile/TransactionsCard";
import ExchangeOperationsCard from "../../components/profile/ExchangeOperationsCard";
import {useDispatch, useSelector} from "react-redux";
import api from "../../api/axiosClient";
import {App} from "antd";
import PayoutsCard from "../../components/profile/PayoutsCard";
import {fetchProfile, selectUser} from "../../store/authSlice";

const EXCHANGE_TAB_TO_STATUSES = {
  OPEN: ["CREATED", "IN_PROGRESS"],
  COMPLETED: ["COMPLETED"],
  DISPUTE: ["DISPUTE"],
  CANCELLED: ["CANCELLED"],
};

function extractResults(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

function useLoadList({url, setData, setLoading, onError}) {
  useEffect(() => {
    const ctrl = new AbortController();

    (async () => {
      setLoading(true);
      try {
        const res = await api.get(url, {signal: ctrl.signal});
        setData(extractResults(res?.data));
      } catch (e) {
        // axios >= 1 поддерживает signal; при abort будет e.name === "CanceledError"
        if (e?.name === "CanceledError") return;
        setData([]);
        onError?.(e);
      } finally {
        // важно: если abort, не трогаем state
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [url, setData, setLoading, onError]);
}

export default function Profile() {
  useEffect(() => {
    document.title = "USDSeller | Профиль";
  }, []);

  const {message} = App.useApp();

  const [paymentsTab, setPaymentsTab] = useState("in");
  const [exchangesTab, setExchangesTab] = useState("OPEN");

  const [txLoading, setTxLoading] = useState(false);
  const [txRows, setTxRows] = useState([]);

  const [payMethods, setPayMethods] = useState([]);
  const [payMethodsLoading, setPayMethodsLoading] = useState(false);

  const [exchangesLoading, setExchangesLoading] = useState(false);
  const [exchangesPayments, setExchangesPayments] = useState([]);

  const [types, setTypes] = useState([]);

  const user = useSelector(selectUser);
  const dispatch = useDispatch();

  const profile = useMemo(() => {
    return {
      full_name: user?.full_name ?? user?.fullName ?? user?.name ?? null,
      email: user?.email ?? null,
      tech_email: user?.technical_email ?? user?.tech_email ?? null,
      balance: user?.balance ?? null,
      client_type: user?.role_display ?? user?.role ?? null,
      commission_type: user?.commission_type ?? "Не установлено",
      commission_percent: user?.commission_percent ?? 0,
      base_currency: user?.main_currency ?? null,
    };
  }, [user]);

  const payoutsRows = useMemo(() => (Array.isArray(user?.payouts) ? user.payouts : []), [user]);

  const onCreatePayout = useCallback(
    async (payload) => {
      try {
        await api.post("/exh/user-payments/", payload);
        message.success("Реквизит добавлен");
        dispatch(fetchProfile());
        return true;
      } catch (e) {
        const status = e?.response?.status;

        if (status === 400) {
          const data = e?.response?.data;
          const text =
            data?.already_exists?.[0] ||
            data?.non_field_errors?.[0] ||
            data?.detail ||
            "Не удалось добавить реквизит";

          message.error(text);
          return false;
        }

        message.error("Ошибка сервера. Попробуйте позже.");
        throw e;
      }
    },
    [message,]
  );

  const onDeletePayout = useCallback(
    async (id) => {
      try {
        await api.delete(`/exh/user-payments/${id}/`);
        message.success("Реквизит удалён");
        dispatch(fetchProfile());
      } catch (e) {
        message.error("Не удалось удалить реквизит");
        throw e;
      }
    },
    [message,]
  );

  const onUpdatePayout = useCallback(
    async (id, payload) => {
      try {
        await api.patch(`/exh/user-payments/${id}/`, payload);
        message.success("Реквизит обновлён");
        dispatch(fetchProfile());
        return true;
      } catch (e) {
        const status = e?.response?.status;
        if (status === 400) {
          const data = e?.response?.data;
          const text =
            data?.already_exists?.[0] ||
            data?.non_field_errors?.[0] ||
            data?.detail ||
            "Не удалось обновить реквизит";
          message.error(text);
          return false;
        }
        message.error("Ошибка сервера. Попробуйте позже.");
        throw e;
      }
    },
    [message]
  );

  const onUpdate = useCallback(
    async (patch) => {
      try {
        await api.patch("/user/profile/", patch);
        dispatch(fetchProfile());
        message.success("Профиль обновлён");
      } catch (e) {
        message.error("Не удалось обновить профиль");
        throw e;
      }
    },
    [dispatch, message]
  );

// currency-methods (с отменой)
  useEffect(() => {
    const ctrl = new AbortController();

    (async () => {
      setPayMethodsLoading(true);
      try {
        const res = await api.get("/exh/currency-methods/", {signal: ctrl.signal});

        const data = extractResults(res?.data); // <-- FIX: поддержка {results: []}

        const opts = data
          .filter((cm) => cm?.is_active && cm?.method?.is_active)
          .map((cm) => ({
            value: cm.id,
            label: `${cm.currency} · ${cm.method?.title ?? cm.method?.code ?? "—"}`,
            icon_url: cm?.method?.icon_url ?? null,
            currency: cm.currency,
            method_code: cm?.method?.code ?? null,
          }));

        setPayMethods(opts);
      } catch (e) {
        if (e?.name === "CanceledError") return;
        setPayMethods([]);
      } finally {
        if (!ctrl.signal.aborted) setPayMethodsLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, []);

  // payouts choices (с отменой)
  useEffect(() => {
    const ctrl = new AbortController();
    api
      .get("/exh/user-payments/choices/", {signal: ctrl.signal})
      .then((r) => setTypes(r.data))
      .catch((e) => {
        if (e?.name === "CanceledError") return;
      });
    return () => ctrl.abort();
  }, []);

  const onTxError = useCallback(() => {
    message.error("Не удалось загрузить транзакции");
  }, [message]);

  const onExhError = useCallback(() => {
    message.error("Не удалось загрузить обмены");
  }, [message]);

  useLoadList({
    url: "/v2/transactions-profile/",
    setData: setTxRows,
    setLoading: setTxLoading,
    onError: onTxError,
  });

  useLoadList({
    url: "/exh/order-transactions/",
    setData: setExchangesPayments,
    setLoading: setExchangesLoading,
    onError: onExhError,
  });

  const allTxRows = useMemo(() => {
    const items = Array.isArray(txRows) ? txRows : [];

    // если occurred_at ISO — сортируем по времени быстрее и корректнее
    const sorted = [...items].sort((a, b) => {
      const ta = Date.parse(String(a?.occurred_at ?? "")) || 0;
      const tb = Date.parse(String(b?.occurred_at ?? "")) || 0;
      return tb - ta;
    });

    return sorted.map((p) => ({
      id: p.id,
      code: p.code ?? null,
      kind: p.kind,
      amount: Number(p.amount),
      currency: p.currency ?? "USD",
      sender_name: p.kind === "OUT" ? (p.sender_name ?? "USDSeller") : (p.sender_name ?? null),
      balance_after: p.balance_after ?? null,
      occurred_at: p.occurred_at ?? null,
    }));
  }, [txRows]);

  const paymentRows = useMemo(() => {
    const kind = paymentsTab === "in" ? "IN" : "OUT";
    return allTxRows.filter((t) => t.kind === kind);
  }, [paymentsTab, allTxRows]);

  const exchanges = useMemo(() => {
    const arr = Array.isArray(exchangesPayments) ? exchangesPayments : [];
    return arr.map((p) => {
      const pairObj = p?.pair;
      const fromCurrency = pairObj?.currency_from ?? "NaN";
      const toCurrency = pairObj?.currency_to ?? "NaN";

      return {
        id: p?.id,
        code: p?.code ?? null,
        giveAmount: Number(p?.amount_from ?? 0),
        giveCurrency: fromCurrency,
        getAmount: Number(p?.amount_to ?? 0),
        getCurrency: toCurrency,
        pair: pairObj ? `${fromCurrency} → ${toCurrency}` : (p?.pair_label ?? "NaN"),
        rate: {buy: p?.buy_rate ?? null, sell: p?.sell_rate ?? null},
        time: p?.updated_at ?? p?.created_at ?? "-",
        status: p?.status ?? "NaN",
      };
    });
  }, [exchangesPayments]);

  const paymentAmountMeta = useMemo(() => {
    return paymentsTab === "in"
      ? {icon: <ArrowDownOutlined/>, cls: "text-green-600"}
      : {icon: <ArrowUpOutlined/>, cls: "text-red-600"};
  }, [paymentsTab]);

  const exchangeRows = useMemo(() => {
    const allowed = EXCHANGE_TAB_TO_STATUSES[exchangesTab] || [];
    return exchanges.filter((e) => allowed.includes(e.status));
  }, [exchangesTab, exchanges]);

  return (
    <div className="px-4">
      <div className="max-w-[360px] lg:max-w-[1400px] mx-auto">
        <ProfileHeader/>

        <ProfileCard profile={profile} onUpdate={onUpdate}/>
        <PayoutsCard
          loading={false}
          types={types}
          payMethods={payMethods}
          payMethodsLoading={payMethodsLoading}
          rows={payoutsRows}
          onCreate={onCreatePayout}
          onUpdate={onUpdatePayout}
          onDelete={onDeletePayout}
        />

        <TransactionsCard
          tab={paymentsTab}
          onTabChange={setPaymentsTab}
          rows={paymentRows}
          amountIcon={paymentAmountMeta.icon}
          amountClassName={paymentAmountMeta.cls}
          loading={txLoading}
        />

        <ExchangeOperationsCard
          tab={exchangesTab}
          onTabChange={setExchangesTab}
          rows={exchangeRows}
          loading={exchangesLoading}
        />
      </div>
    </div>
  );
}